import { graphHooks } from '@tunasong/graph-lib/react'
import { logger } from '@tunasong/models'
import type { Calendar, Entity, ManyOfManyPoll, ManyOfManyPollResponse, Persisted } from '@tunasong/schemas'
import { EntitySelect } from '@tunasong/tunasong-ui'
import { ConfirmDialog } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'
import { buildCalendarEvents, formatAddToCalendarLabel } from './build-calendar-events.js'
import { formatSlotLabel } from './date-slot.js'
import styles from './add-to-calendar-dialog.module.css'

export interface AddToCalendarDialogProps {
  poll: Persisted<ManyOfManyPoll>
  open: boolean
  onClose(): void
  /**
   * Reported once every event has actually been written, with the calendar that received them.
   * Never on the click alone: an optimistic cache renders a rejected create exactly like an
   * accepted one, so a poll marked as handed off on the click would claim a calendar that
   * never got the dates.
   */
  onAdded?(calendarId: string): void
}

/**
 * Hands the winning slots to the calendar.
 *
 * Resolve, do not request, when there is only one answer: a band usually has exactly one
 * calendar, and asking a question with a single possible answer is friction rather than
 * confirmation.
 *
 * **The candidate set is the poll's OWN bandspace calendars**, reached through the
 * `relation: 'calendar'` edge that `plugins/bandspace/src/setup.ts:36` writes — never every
 * calendar the user can see. Each bandspace creates its own `Band calendar`, so an unscoped
 * set had two failures: anyone in two or more bands always saw the picker, and a user in one
 * band could resolve to a *different* band's calendar and silently write the rehearsal dates
 * there. A wrong destination nobody notices is worse than an extra click.
 *
 * A poll nested below its bandspace resolves nothing and falls through to the picker, which
 * is the correct conservative answer: better to ask than to guess a destination.
 */
export const AddToCalendarDialog: FC<AddToCalendarDialogProps> = props => {
  const { poll, open, onClose, onAdded } = props

  const { edgeEntities: calendarEdges = [] } = graphHooks.useEdgeEntities<Entity, Calendar>({
    sourceId: poll.parentId,
    relation: 'calendar',
  })
  const { entities: responses = [] } = graphHooks.useEntityChildren<ManyOfManyPollResponse>({
    parentId: poll.id,
    filter: 'pollresponse',
  })
  const { members } = graphHooks.useMembers(poll)
  const { profile: organizer } = graphHooks.useProfile()
  const { createEntity } = graphHooks.useEntityGraph()

  const [chosenId, setChosenId] = useState<string | undefined>(undefined)
  const { entity: chosen } = graphHooks.useEntity<Calendar>(chosenId)

  const inScope = calendarEdges.map(edge => edge.target)
  const resolved = inScope.length === 1 ? inScope[0] : undefined
  const target = resolved ?? chosen

  const events = organizer ? buildCalendarEvents({ poll, responses, members, organizer }) : []

  const handleConfirm = async () => {
    if (!target) {
      return
    }

    try {
      for (const entity of events) {
        await createEntity({ parent: target, entity }).unwrap()
      }
    } catch (error) {
      /** Leave the dialog open: the owner's way to retry is the button they just pressed. */
      logger.warn('Failed to add the rehearsals to the calendar', error)
      return
    }

    onAdded?.(target.id)
    onClose()
  }

  return (
    <ConfirmDialog
      open={open}
      title="Add to calendar"
      text={events.length === 1 ? 'One rehearsal will be added.' : `${events.length} rehearsals will be added.`}
      okText={formatAddToCalendarLabel(target, events.length)}
      enabled={Boolean(target) && events.length > 0}
      onCancel={onClose}
      onConfirm={handleConfirm}
    >
      <ul className={styles.list}>
        {events.map(event => (
          <li key={event.id}>{`${formatSlotLabel(event.start ?? '')} — ${event.attendees?.length ?? 0} attending`}</li>
        ))}
      </ul>

      {/* Only when the target could not be resolved to a single calendar. */}
      {resolved ? null : (
        <EntitySelect
          buttonText="Choose a calendar"
          entityId={chosenId}
          filter={{ types: ['calendar'] }}
          onSelected={setChosenId}
        />
      )}
    </ConfirmDialog>
  )
}

export default AddToCalendarDialog
